import {observable, action, reaction, runInAction} from 'mobx';
import * as Sentry from '@sentry/react';
import {autobind, writable, nonenumerable} from 'spa/decorators';
import {fromCallback} from 'promise-cb';

import objectAssignDeep from 'utils/object-assign-deep';
import isValidException from 'utils/isValidException';
import braintreeClient from 'braintree-web/client';
import braintreeThreeDSecure from 'braintree-web/three-d-secure';
import braintreePaypal from 'braintree-web/paypal';
import braintreeApplePay from 'braintree-web/apple-pay';

import {addPopup, removePopup} from 'spa/lib/popup';
import APIRequestState from '../_util/api-request';

class BraintreeState extends APIRequestState {
  @observable clientToken;
  @observable merchantID;
  @observable environment;

  @observable isDoingApplePay = false;
  @observable applePayErr = null;
  @observable isDoingPaypal = false;
  @observable paypalError = null;
  @observable canMakeApplePayments = false;

  @action abort() {
    this.closePaypal();
    this.close3DS();
    super.abort();
  }

  @nonenumerable @writable threeDSecure;
  @nonenumerable @writable applePay;
  @nonenumerable @writable paypal;

  @action('initGateway - braintree')
  async initGateway(gatewayInfo, countryId, userId) {
    await this.setupBraintree(gatewayInfo)
      .then((response) => {
        this.clientToken = gatewayInfo.clientToken;
        this.merchantID = gatewayInfo.gatewayMerchantID;
        this.environment = gatewayInfo.environment;

        return response;
      })
      .catch((error) => {
        throw error;
      });
  }

  @action async setupBraintree({clientToken, gatewayMerchantID, environment}) {
    this.closePaypal();
    this.close3DS();
    if (this.threeDSecure) {
      await fromCallback(cb => this.threeDSecure.teardown(cb));
      this.threeDSecure = null;
    }
    if (this.paypal) {
      await fromCallback(cb => this.paypal.teardown(cb));
      this.paypal = null;
    }
    this.client = await fromCallback(cb =>
      braintreeClient.create({authorization: clientToken}, cb));

    if (window && window.ApplePaySession) {
      this.applePay = await fromCallback(cb =>
        braintreeApplePay.create({
          client: this.client,
        }, cb));

      // Check if this browser can make apple pay payments
      if (ApplePaySession.canMakePayments()) {
        // Check with Apple to see if this user has a card available with their wallet
        this.canMakeApplePayments = await ApplePaySession.canMakePaymentsWithActiveCard(this.applePay.merchantIdentifier);
      }
    }
  }

  /**
   * @see https://braintree.github.io/braintree-web/3.6.0/ThreeDSecure.html#verifyCard
   * @see https://braintree.github.io/braintree-web/3.6.0/ThreeDSecure.html#~verifyPayload
   */
  @action async verify3DS(options) {
    if (!this.threeDSecure) {
      this.threeDSecure = await fromCallback(cb =>
        braintreeThreeDSecure.create({
          client: this.client,
        }, cb));
    }

    runInAction(() => {
      this.isDoing3DS = true;
    });

    let iframeContainer;
    return fromCallback(cb => this.threeDSecure.verifyCard({
      ...options,
      addFrame(err, iframe) {
        if (err) {
          cb(err);
          return;
        }
        iframeContainer = addPopup(iframe);
      },
      removeFrame() {
        removePopup(iframeContainer);
      },
    }, action('3ds verifyCard callback', (err, response) => {
      this.isDoing3DS = false;
      cb(err, response);
    })));
  }

  @action close3DS() {
    if (this.isDoing3DS) {
      this.threeDSecure.cancelVerifyCard();
    }
  }

  /**
   * Convert card details into a nonce
   */
  @action tokenizeCard(creditCard) {
    const {
      billingAddress,
      cvv,
      expirationMonth,
      expirationYear,
      number,
    } = creditCard;

    return fromCallback(cb =>
      this.client.request({
        method: 'post',
        endpoint: 'payment_methods/credit_cards',
        data: {
          creditCard: {
            billingAddress,
            cvv,
            expirationMonth,
            expirationYear,
            number,
          },
        },
      }, cb))
      .then(({creditCards: [card]}) => card)
      .catch((error) => {
        throw error;
      });
  }

  @action async tokenizeApplePay(event) {
    if (this.isDoingApplePay || !this.applePay) {
      return null;
    }

    runInAction(() => {
      this.isDoingApplePay = true;
      this.applePayErr = null;
    });

    return fromCallback((cb) => {
      this.applePayTokenizeReturn = this.applePay.tokenize({
        token: event.payment.token,
      }, cb);
    });
  }

  /*
   *  getApplePayPaymentData - get the object with the payment data
   */
  @action('validateApplePay - BillingState')
  async getApplePayPaymentData(data) {
    return new Promise(((resolve, reject) => {
      this.tokenizeApplePay(data)
        .then(payload => (
          resolve({
            chargeTokenData: {
              gateway: 'braintree',
              gatewayTokenizationType: 'apple-pay',
              paymentMethodNonce: payload.nonce,
            },
            paymentType: 'charge',
            clientSupports3DSecure: true,
            passed3DSecure: true,
          })
        ))
        .catch(error => reject(new Error(error)));
    }));
  }

  @action createPaymentRequest(data) {
    const {total} = data;

    return this.applePay.createPaymentRequest({
      total,
    });
  }

  @action async validateApplePay(event) {
    if (!this.applePay) {
      return null;
    }

    return fromCallback((cb) => {
      this.applePayValidationReturn = this.applePay.performValidation({
        validationURL: event.validationURL,
        displayName: 'Urban Massage',
      }, cb);
    });
  }

  /**
   * @see https://braintree.github.io/braintree-web/current/PayPal.html#tokenize
   */
  @action async tokenizePaypal(homeCountry_id) {
    if (this.isDoingPaypal) {
      this.paypalTokenizeReturn.focus();
      return null;
    }

    if (!this.paypal) {
      this.paypal = await fromCallback(cb =>
        braintreePaypal.create({
          client: this.client,
        }, cb));
    }

    runInAction(() => {
      this.isDoingPaypal = true;
      this.paypalError = null;
    });

    return new Promise(((resolve, reject) => {
      this.paypalTokenizeReturn = this.paypal.tokenize({
        flow: 'vault',
        /* translators: for a list of supported locales, see https://braintree.github.io/braintree-web/current/PayPal.html#tokenize */
        locale: this.ctx.i18n._x('en_GB', 'paypal locale'),
      }, (err, response) => {
        if (err) {
          if (!this.ctx.i18n.isKnownError(err)) {
            // This is unexpected so record it in Sentry
            if (isValidException(err)) {
              Sentry.captureException(err);
            } else {
              Sentry.captureMessage('Paypal tokenisation failure');
            }
          }

          return reject(err);
        }

        const chargeTokenData = {
          gateway: 'braintree',
          gatewayTokenizationType: 'paypal',
          paymentMethodNonce: response.nonce,
        };

        // Save to account
        this.ctx.billing.addv2({}, {
          country_id: homeCountry_id,
          extraGatewayData: {},
          chargeTokenData,
        }).then((res) => {
          resolve(res);
        }, (error) => {
          if (!this.ctx.i18n.isKnownError(error)) {
            // This is unexpected so record it in Sentry
            if (isValidException(error)) {
              Sentry.captureException(error);
            } else {
              Sentry.captureMessage('Braintree billing.add failure');
            }
          }

          return reject(error);
        }).catch((error) => {
          if (!this.ctx.i18n.isKnownError(error)) {
            // This is unexpected so record it in Sentry
            if (isValidException(error)) {
              Sentry.captureException(error);
            } else {
              Sentry.captureMessage('Braintree billing.add failure');
            }
          }

          return reject(error);
        });
      });
    }));
  }

  @action closePaypal() {
    if (this.isDoingPaypal) {
      this.paypalTokenizeReturn.close();
      this.paypalTokenizeReturn = null;
      this.isDoingPaypal = false;
      this.paypalError = null;
    }
  }

  @action resetPaypal() {
    this.closePaypal();
    this.isDoingPaypal = false;
    this.paypalError = null;
  }

  @action resetApplePay() {
    this.isDoingApplePay = false;
    this.applePayErr = null;
  }

  @action deserialize(value) {
    const {client, ...rest} = value;
    objectAssignDeep(this, rest);
    if (client) {
      braintreeClient.create(client, action('braintree deserialize callback', (err, client) => {
        this.client = client;
      }));
    }
  }

  @action setup() {
    this.listeners = [];
  }

  @action dispose() {
    this.close3DS();
    this.closePaypal();
    if (this.threeDSecure) {
      this.threeDSecure.teardown();
    }
    if (this.applePay) {
      this.applePay.teardown();
    }
    if (this.paypal) {
      this.paypal.teardown();
    }
    while (this.listeners && this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default BraintreeState;



// WEBPACK FOOTER //
// ./spa/state/payments/braintree.jsx